/* eslint-disable camelcase */
import { default as uniqid } from 'uniqid'; // eslint-disable-line import/no-named-default
import type { Dictionary } from '@nab/x-types';
import { AuthToken, DafAuthConfig, DafExchangeAuthToken, DafScopeConfig, DafSeedAuthConfig } from '../types';
import { authenticate } from '../core';
import { DafTokenExchangeError, exchangeDafToken } from '../utils/exchangeDafToken';
import { getScopes } from '../utils/getScopes';
import { getUrlAndFolderPath } from '../utils/getUrlAndFolderPath';
import { DafAuthStrategy } from './DafAuthStrategy';
import { MAX_RETRY_COUNT } from '../constants';

/**
 * Exchanges a DAF seed token for a mini-app token
 */
export class DafExchangeStrategy extends DafAuthStrategy {
    protected scopesCache: Dictionary<DafScopeConfig> = {};

    constructor(dafEndpoint: string) {
        super(dafEndpoint);
    }

    /**
     * "I can authenticate you if you require a DAF Bearer token"
     */
    public supports(config: DafAuthConfig): boolean {
        return (
            super.supports(config) &&
            !!(config.tokenType === 'Bearer' && config.appMetaData && config.appMetaData.assetManifestUrl && !(<any>config).isSeed)
        );
    }

    private async getSeedToken(config: DafAuthConfig, forceRefresh: boolean = false) {
        const seedToken = await authenticate<DafSeedAuthConfig>(
            {
                ...config,
                appMetaData: undefined,
                tokenType: 'Bearer',
                tokenSource: 'DAF',
                isSeed: true
            },
            forceRefresh
        );
        return seedToken;
    }

    /**
     * Retrieves an app auth token using the SPA's seed token
     */
    public async authenticate(config: DafAuthConfig, retryCount: number = 0): Promise<DafExchangeAuthToken> {
        let seedToken: AuthToken<DafAuthConfig>;

        try {
            /**
             * Attempt to obtain the seed token via the auth manager
             *
             * Basically calls this very function again to request the seed token but looks for it in the cache first
             * to save us the overhead of repeating the cache & storage logic here.
             */
            seedToken = await this.getSeedToken(config);
        } catch (e) {
            throw new Error(`Failed to obtain seed token`);
        }

        let scopes: DafScopeConfig = this.scopesCache[config.appMetaData.assetManifestUrl];

        if (!scopes) {
            try {
                const retrievedScopes: DafScopeConfig[] = await getScopes(getUrlAndFolderPath(config.appMetaData.assetManifestUrl));
                scopes = retrievedScopes && retrievedScopes.find((s) => s && s.context === config.context);
            } catch (e) {
                if (e && e.message === '404') {
                    scopes = undefined;
                } else {
                    throw new Error(`Failed to retrieve scopes: \n${e && e.message}`);
                }
            }
        }

        // Return seed token if scopes.json wasn't found, otherwise perform token exchange
        if (!scopes) {
            return {
                ...seedToken,
                id: uniqid(),
                parentId: seedToken.id,
                config: {
                    environment: seedToken.config.environment,
                    tokenType: seedToken.config.tokenType,
                    tokenSource: seedToken.config.tokenSource,
                    context: seedToken.config.context,
                    scopes: seedToken.config.scopes,
                    clientId: seedToken.config.clientId
                }
            };
        }

        this.scopesCache[config.appMetaData.assetManifestUrl] = scopes;

        return this.exchangeToken(config, seedToken, scopes, retryCount);
    }

    /**
     * Exchange seed token for app token
     */
    protected async exchangeToken(
        config: DafAuthConfig,
        seedToken: AuthToken<DafAuthConfig>,
        scopeConfig: DafScopeConfig,
        retryCount: number
    ): Promise<DafExchangeAuthToken> {
        if (!scopeConfig.clientId) {
            throw new Error('Mini-app client id is missing');
        }
        try {
            const responseData = await exchangeDafToken(seedToken.tokenValue, scopeConfig, this.dafEndpoint, config.additionalRequestHeaders);
            const { access_token, idleTimeout, expires_in } = responseData;
            return {
                id: uniqid(),
                parentId: seedToken.id,
                tokenValue: access_token,
                idleTimeout: idleTimeout ? idleTimeout * 1000 : undefined,
                issuedAt: Date.now(),
                expires: expires_in ? Date.now() + expires_in * 1000 : undefined,
                config: {
                    ...config,
                    ...scopeConfig
                }
            };
        } catch (e) {
            // @todo handle scenarios by where DAF has invalidated tokens but we don't know about it yet
            if (e.message === DafTokenExchangeError.INVALID_SEED_TOKEN) {
                // Force refresh the seed token and eventually app token
                if (retryCount < MAX_RETRY_COUNT) {
                    const newSeedToken = await this.getSeedToken(config, true);
                    return this.exchangeToken(config, newSeedToken, scopeConfig, retryCount + 1);
                }
                throw e;
            } else {
                return this.handleDafError(e, config, retryCount);
            }
        }
    }
}
